// Google Analytics 4 Service for ConrarianOS
// Handles GA4 tracking with proper exclusions for admin users and contrarianthink.com emails

interface GA4Config {
  measurementId: string;
  enableDebug?: boolean;
}

// GA4 Event interface for future use
// interface GA4Event {
//   eventName: string;
//   parameters?: Record<string, any>;
// }

interface GA4PageView {
  page_title: string;
  page_location: string;
  page_path: string;
  user_role?: string;
  has_company?: boolean;
}

interface GA4ToolInteraction {
  tool_name: string;
  action: string;
  section?: string;
  value?: number;
  user_role?: string;
}

interface GA4VideoEvent {
  video_id: string;
  video_title?: string;
  course_id?: string;
  action: 'play' | 'pause' | 'progress' | 'complete';
  progress_percentage?: number;
  duration?: number;
  user_role?: string;
}

class GA4Service {
  private config: GA4Config;
  private isInitialized = false;
  private isEnabled = false;

  constructor(config: GA4Config) {
    this.config = config;
  }

  /**
   * Initialize GA4 - should only be called in production environment
   * and for non-admin, non-contrarianthink.com users
   */
  async initialize(userEmail?: string, userRole?: string): Promise<void> {
    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }

    // Only initialize in production
    const isProduction = import.meta.env['VITE_APP_ENV'] === 'production';
    if (!isProduction) {
      console.log('GA4: Skipping initialization - not in production environment');
      return;
    }

    // Skip admin users and contrarianthink.com emails
    if (this.shouldSkipTracking(userEmail, userRole)) {
      console.log('GA4: Skipping initialization - admin user or contrarianthink.com email');
      return;
    }

    try {
      // Load gtag if not already loaded
      if (!window.gtag) {
        await this.loadGtagScript();
      }

      // Configure GA4
      window.gtag?.('config', this.config.measurementId, {
        // Enhanced measurement settings
        enhanced_measurement: true,
        // Privacy settings
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        // Custom configuration
        custom_map: {
          custom_parameter_1: 'user_role',
          custom_parameter_2: 'tool_name'
        }
      });

      this.isInitialized = true;
      this.isEnabled = true;

      if (this.config.enableDebug) {
        console.log('GA4: Initialized successfully', this.config.measurementId);
      }

    } catch (error) {
      console.error('GA4: Failed to initialize', error);
    }
  }

  /**
   * Check if tracking should be skipped for this user
   */
  private shouldSkipTracking(userEmail?: string, userRole?: string): boolean {
    // Skip if no user info available
    if (!userEmail) return true;

    // Skip contrarianthink.com emails
    if (userEmail.endsWith('@contrarianthink.com')) return true;

    // Skip admin roles
    if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'ct_team') return true;

    return false;
  }

  /**
   * Check if current path is admin section
   */
  private isAdminPath(path: string): boolean {
    return path.startsWith('/admin');
  }

  /**
   * Load Google Analytics gtag script
   */
  private async loadGtagScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create gtag script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
      
      script.onload = () => {
        // Initialize gtag function
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer?.push(arguments);
        };
        window.gtag('js', new Date());
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load gtag script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title: string, userRole?: string, hasCompany?: boolean): void {
    if (!this.isEnabled || this.isAdminPath(path)) return;

    const pageViewData: GA4PageView = {
      page_title: title,
      page_location: window.location.href,
      page_path: path,
    };

    // Add user context (non-PII)
    if (userRole) pageViewData.user_role = userRole;
    if (hasCompany !== undefined) pageViewData.has_company = hasCompany;

    window.gtag?.('event', 'page_view', pageViewData);

    if (this.config.enableDebug) {
      console.log('GA4: Page view tracked', pageViewData);
    }
  }

  /**
   * Track tool interaction
   */
  trackToolInteraction(toolName: string, action: string, section?: string, value?: number, userRole?: string): void {
    if (!this.isEnabled) return;

    const eventData: GA4ToolInteraction = {
      tool_name: toolName,
      action: action,
    };

    if (section) eventData.section = section;
    if (value !== undefined) eventData.value = value;
    if (userRole) eventData.user_role = userRole;

    window.gtag?.('event', 'tool_interaction', eventData);

    if (this.config.enableDebug) {
      console.log('GA4: Tool interaction tracked', eventData);
    }
  }

  /**
   * Track video events
   */
  trackVideoEvent(
    videoId: string, 
    action: 'play' | 'pause' | 'progress' | 'complete',
    options?: {
      videoTitle?: string;
      courseId?: string;
      progressPercentage?: number;
      duration?: number;
      userRole?: string;
    }
  ): void {
    if (!this.isEnabled) return;

    const eventData: GA4VideoEvent = {
      video_id: videoId,
      action: action,
    };

    if (options?.videoTitle) eventData.video_title = options.videoTitle;
    if (options?.courseId) eventData.course_id = options.courseId;
    if (options?.progressPercentage !== undefined) eventData.progress_percentage = options.progressPercentage;
    if (options?.duration !== undefined) eventData.duration = options.duration;
    if (options?.userRole) eventData.user_role = options.userRole;

    // Use specific event names for different video actions
    const eventName = `video_${action}`;
    window.gtag?.('event', eventName, eventData);

    if (this.config.enableDebug) {
      console.log(`GA4: Video ${action} tracked`, eventData);
    }
  }

  /**
   * Track custom event
   */
  trackCustomEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isEnabled) return;

    window.gtag?.('event', eventName, parameters);

    if (this.config.enableDebug) {
      console.log('GA4: Custom event tracked', { eventName, parameters });
    }
  }

  /**
   * Set user properties (non-PII only)
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isEnabled) return;

    // Filter out any potential PII
    const safeProperties = this.sanitizeUserProperties(properties);
    
    window.gtag?.('config', this.config.measurementId, {
      custom_map: safeProperties
    });

    if (this.config.enableDebug) {
      console.log('GA4: User properties set', safeProperties);
    }
  }

  /**
   * Remove any potential PII from user properties
   */
  private sanitizeUserProperties(properties: Record<string, any>): Record<string, any> {
    const safe: Record<string, any> = {};
    const allowedKeys = ['user_role', 'has_company', 'account_type', 'subscription_status'];
    
    for (const [key, value] of Object.entries(properties)) {
      if (allowedKeys.includes(key)) {
        safe[key] = value;
      }
    }
    
    return safe;
  }

  /**
   * Check if GA4 is enabled and initialized
   */
  get enabled(): boolean {
    return this.isEnabled && this.isInitialized;
  }
}

// Global GA4 instance
export const ga4Service = new GA4Service({
  measurementId: 'G-PNJQH64Z75',
  enableDebug: import.meta.env.DEV
});

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export default ga4Service; 